import React, { ReactElement, useState } from "react";
import loadable from "@loadable/component";
import {
  Button,
  ButtonVariant,
  IconName
} from "@silversea-ssc/ui-components-react";
import { Trans } from "@lingui/macro";
import {
  BrochureCtaFragment,
  ButtonCtaFragment,
  FileCtaFragment,
  LinkCtaFragment,
  RaqCtaFragment,
  VideoCtaFragment
} from "types/graphql-types";
import PageBuilderPageCTAButton from "components/PageBuilder/PageBuilderPageCTAButton";
import getUrlsByLanguage from "src/urls/urls";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { isPresent } from "src/utils/checks";

const VideoModal = loadable(() => import("components/Video/VideoModal"));

export type PageBuilderButtonCTAProps = Partial<ButtonCtaFragment> & {
  defaultButtonVariant?: ButtonVariant;
};

const defaultIconForType = (type: ButtonVariant): IconName | undefined => {
  if (type == "link") return "arrow-right";
  return undefined;
};

const RaqCTAButton = (props: RaqCtaFragment & { iconName?: IconName }) => {
  const language = useLanguage();
  const urls = getUrlsByLanguage(language.code);
  const defaultCTALabel = <Trans>Request a Quote</Trans>;
  const variant = (props.variant || "primary") as ButtonVariant;

  return (
    <Button
      renderAs="a"
      href={urls.serviceLinks.raq.href}
      variant={variant}
      data-content-piece="request_a_quote"
      iconName={props.iconName}
    >
      {props.label?.localized || defaultCTALabel}
    </Button>
  );
};

const BrochureCTAButton = (
  props: BrochureCtaFragment & { iconName?: IconName }
) => {
  const language = useLanguage();
  const urls = getUrlsByLanguage(language.code);
  const defaultCTALabel = <Trans>View the Brochures</Trans>;
  const link = props.brochure?.fullPath || urls.serviceLinks.brochures.href;
  const variant = (props.variant || "primary") as ButtonVariant;
  return (
    <Button
      renderAs="a"
      href={link}
      variant={variant}
      data-content-piece="view_the_brochures"
      iconName={props.iconName}
    >
      {props.label?.localized || defaultCTALabel}
    </Button>
  );
};

const FileCTAButton = (props: FileCtaFragment) => {
  const fileUrl = props.file?.localized?.asset?.url;
  const defaultCTALabel = <Trans id="cta-download">Download</Trans>;
  return (
    <Button
      renderAs="a"
      variant={(props.variant || "primary") as ButtonVariant}
      href={`${fileUrl}?dl=`}
      iconName="download"
      rel="nofollow"
      data-content-piece="download"
      download
    >
      {props.label?.localized || defaultCTALabel}
    </Button>
  );
};

const LinkCTAButton = (props: LinkCtaFragment & { iconName?: IconName }) => {
  const url = props.url?.localized;
  const defaultCTALabel = <Trans id="cta-discover-more">Discover more</Trans>;
  const variant = (props.variant || "primary") as ButtonVariant;

  return (
    <Button
      renderAs="a"
      variant={variant}
      href={url}
      data-content-piece="discover_more"
      iconName={props.iconName}
    >
      {props.label?.localized || defaultCTALabel}
    </Button>
  );
};

const VideoCTAButton = (props: VideoCtaFragment) => {
  const defaultCTALabel = <Trans id="watch the video">Watch The Video</Trans>;
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  if (!props.video?.file) return null;

  return (
    <React.Fragment>
      {isVideoOpen && (
        <VideoModal
          onRequestClose={() => setIsVideoOpen(false)}
          videoFile={props.video?.file}
          trackingTitle={props.video?.trackingTitle}
        />
      )}
      <Button
        renderAs="button"
        onClick={() => setIsVideoOpen(true)}
        variant={(props.variant || "primary") as ButtonVariant}
        iconName="play"
        data-content-piece="watch_the_video"
      >
        {props.label?.localized || defaultCTALabel}
      </Button>
    </React.Fragment>
  );
};

const PageBuilderButtonCTA: React.FC<PageBuilderButtonCTAProps> = (props) => {
  const { defaultButtonVariant = "primary" } = props;
  const variant = "variant" in props ? props.variant : undefined;
  const buttonVariant = variant || defaultButtonVariant;
  const iconName = defaultIconForType(buttonVariant as ButtonVariant);
  if (props.__typename == "SanityPageCTA")
    return (
      <PageBuilderPageCTAButton
        {...props}
        iconName={iconName}
        variant={buttonVariant}
      />
    );
  if (props.__typename == "SanityRaqCTA")
    return (
      <RaqCTAButton {...props} iconName={iconName} variant={buttonVariant} />
    );
  if (props.__typename == "SanityBrochureCTA")
    return (
      <BrochureCTAButton
        {...props}
        iconName={iconName}
        variant={buttonVariant}
      />
    );
  if (props.__typename == "SanityFileCTA")
    return <FileCTAButton {...props} variant={buttonVariant} />;

  if (props.__typename == "SanityVideoCTA")
    return <VideoCTAButton {...props} variant={buttonVariant} />;

  if (props.__typename == "SanityLinkCTA")
    return (
      <LinkCTAButton {...props} iconName={iconName} variant={buttonVariant} />
    );

  return null;
};

export const renderAllButtons = (
  buttons: (ButtonCtaFragment | undefined)[] | undefined
): ReactElement[] | undefined => {
  const filteredButtons = buttons?.filter(isPresent);
  if (!filteredButtons || !(filteredButtons.length > 0)) return undefined;

  return filteredButtons.map((button) => (
    <PageBuilderButtonCTA key={button?._key} {...button} />
  ));
};

export default PageBuilderButtonCTA;
